import { makeStyles } from '@material-ui/core';
import FundFamilyIcon from '@material-ui/icons/AccountBalance';
import React from 'react';
import {
  Datagrid,
  List,
  ReferenceManyField,
  Responsive,
  TextInput,
  SelectInput,
  SimpleList,
  TextField,
  TopToolbar,
  Pagination
} from 'react-admin';
import omit from 'lodash/omit';

import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import ListTitleBar from '../../core/components/ListTitleBar';
import StatusSelectField from '../../core/components/StatusSelectField';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import FundFamilyFundDatagrid from './fundfamiliesfund/FundFamilyFundDatagrid';
import { fundFamilyStatus } from './FundFamilyLookups';

const useStyles = makeStyles({
  expandOnlyDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      }
    }
  },
  textInput: {
    minWidth: '225px'
  },
  expandDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      },
      '& th:nth-of-type(2)': {
        width: '24px'
      }
    }
  },
  expandIconCell: {
    width: '14px',
    '& div': {
      width: '14px',
      padding: '0'
    },
    '& div:hover ': {
      backgroundColor: 'transparent'
    }
  },
  root: {
    marginRight: 'auto'
  },
  statusColumnStyle: {
    width: '68px'
  }
});

const useshowStyles = makeStyles({
  root: {
    padding: '16px 16px 10px 16px'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();
  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const FundFamilyFilters = props => {
  const classes = useStyles();

  return (
    <Filter variant="standard" {...props}>
      <TextInput
        label="Search by Fund Family Name"
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        className={classes.textInput}
      />
      <TextInput
        label="Fund Family ID"
        source="id"
        allowEmpty
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <SelectInput
        label="Status"
        source="status"
        choices={fundFamilyStatus}
        allowEmpty
      />
    </Filter>
  );
};

const FundFamilyShow = props => {
  const classes = useshowStyles();
  const handleRowClick = fundFamilyId => {
    return `/funds/${fundFamilyId}`;
  };

  const { permissions, ...rest } = props;

  return (
    <div className={classes.root}>
      <ReferenceManyField
        label=""
        reference="funds"
        target="fundFamilyId"
        sort={{ field: 'name', order: 'ASC' }}
        {...rest}
      >
        <FundFamilyFundDatagrid
          handleRowClick={handleRowClick}
          expand={true}
          permissions={permissions}
        />
      </ReferenceManyField>
    </div>
  );
};

const FundFamilyTitle = props => {
  return (
    <ListTitleBar
      type="Fund Families"
      title="Create New Fund Family"
      createUrl="#/fundfamilies/create"
      icon={<FundFamilyIcon />}
      hasCreatePermission={props.hasCreatePermission}
    />
  );
};

const FundFamilyList = props => {
  const classes = useStyles();
  const {
    permissions = [],
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    ...rest
  } = props;

  return (
    <List
      {...omit(rest, ['userProfile'])}
      sort={{ field: 'name', order: 'ASC' }}
      filter={{ orphanVisibility: 'ALL_VISIBLE' }}
      exporter={false}
      title={<FundFamilyTitle hasCreatePermission={hasCreatePermission} />}
      filters={<FundFamilyFilters />}
      perPage={20}
      pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
      bulkActionButtons={hasDeletePermission && <BulkActionDeleteButton />}
      actions={<Actions />}
      empty={
        <Empty
          emptyText="There are no Fund Families to be displayed"
          title="Fund Families"
        />
      }
      syncWithLocation
    >
      <Responsive
        small={
          <SimpleList
            primaryText={record => (record?.name ? record.name : '')}
            secondaryText={record => (record?.id ? record.id : '')}
            tertiaryText={() => ''}
          />
        }
        medium={
          <Datagrid
            classes={{
              thead: hasDeletePermission
                ? classes.expandDataGridHead
                : classes.expandOnlyDataGridHead,
              expandIconCell: classes.expandIconCell
            }}
            expand={<FundFamilyShow permissions={permissions} />}
          >
            <LinkField
              label="Fund Family Name"
              source="name"
              id="Fund Family Name"
            />
            <TextField label="A.K.A." source="aka" id={'A.K.A'} />
            <TextField label="Fund Family ID" source="id" id="Fund Family ID" />
            <StatusSelectField
              id="Status"
              label="Status"
              source="status"
              choices={fundFamilyStatus}
              headerClassName={classes.statusColumnStyle}
            />
          </Datagrid>
        }
      />
    </List>
  );
};

export default FundFamilyList;
